import { cx } from '@twind/core';
import { useState, useEffect, PropsWithChildren, createRef, RefObject, useLayoutEffect } from 'react';

type CustomScrollbarProps = PropsWithChildren<{
  id?: string;
  dataTestId?: string;
  className?: string;
  alignToBottom?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  rememberScrollPosition?: boolean;
}>;

const positions = new Map<string, number>();

const CustomScrollbar = ({
  id,
  dataTestId,
  children,
  className,
  alignToBottom = false,
  containerRef = createRef(),
  rememberScrollPosition = false,
}: CustomScrollbarProps) => {
  const [lastScrollTime, setLastScrollTime] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [thumbHeight, setThumbHeight] = useState('20%');
  const [thumbTop, setThumbTop] = useState('0%');

  const updateThumb = () => {
    if (containerRef.current) {
      setIsScrolling(true);
      setLastScrollTime(Date.now());

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const heightRatio = clientHeight / scrollHeight;
      const thumbHeight = clientHeight * heightRatio;
      const thumbTopPercentage = (scrollTop / clientHeight) * 100;

      if (rememberScrollPosition && id) {
        positions.set(id, scrollTop);
      }

      setThumbHeight(`${thumbHeight}px`);
      setThumbTop(`${thumbTopPercentage}%`);
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isScrolling) {
      timeout = setTimeout(() => {
        if (Date.now() - lastScrollTime > 500) {
          setIsScrolling(false);
        }
      }, 500);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [isScrolling, lastScrollTime]);

  useEffect(() => {
    const contentElement = containerRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', updateThumb);
    }
    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', updateThumb);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (rememberScrollPosition) {
      const contentElement = containerRef.current;
      if (contentElement && id) {
        const position = positions.get(id);
        if (position) {
          contentElement.scrollTop = position;
        }
      }
    }
  }, [rememberScrollPosition, id, containerRef]);

  return (
    <section
      role="region"
      aria-label="Scrollable content"
      id={id}
      data-testid={dataTestId}
      className="relative w-full h-0 grow overflow-hidden flex flex-col"
    >
      <div
        id="custom-scrollbar"
        ref={containerRef}
        className={cx(
          `~(absolute ${
            alignToBottom ? 'bottom-0 max-h-full' : 'top-0 h-full'
          } grow left-0 right-0 overflow-scroll custom-scrollbar-content hide-scrollbars)`,
          className
        )}
        onScroll={updateThumb}
      >
        {children}
      </div>
      <div className="absolute top-0 right-0 bottom-0 w-1 h-full flex flex-col justify-start items-center">
        <div
          className={cx(
            'w-full bg-gray-500 rounded-full cursor-pointer transition-opacity duration-750 ease-in-out transform-opacity',
            isScrolling ? 'opacity-80' : 'opacity-0'
          )}
          style={{
            height: thumbHeight,
            transform: `translateY(${thumbTop})`,
          }}
        />
      </div>
    </section>
  );
};

CustomScrollbar.displayName = 'CustomScrollbar';

export default CustomScrollbar;
